import { useState, useEffect } from 'react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { useLanguage, API } from '@/App';
import { Target, Eye, Heart } from 'lucide-react';
import { seoData } from '@/utils/seoData';

const AboutPage = () => {
  const { language } = useLanguage();
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/site-settings`);
        setSiteSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const staticContent = {
    tr: {
      title: 'Hakkımızda',
      subtitle: 'Endüstriyel otomasyon alanında 15+ yıldır öncü',
    },
    en: {
      title: 'About Us',
      subtitle: 'Pioneering in industrial automation for 15+ years',
    },
  };

  const t = staticContent[language];
  const pageContent = siteSettings?.about_page?.[language] || {};

  const pageSeo = seoData.about[language];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="about-title"
          >
            {t.title}
          </h1>
          <p className="text-lg sm:text-xl text-gray-600" data-testid="about-subtitle">{t.subtitle}</p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Heart className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {pageContent?.story?.title || 'Hikayemiz'}
            </h2>
          </div>
          <p className="text-lg text-gray-700 leading-relaxed" data-testid="about-story">
            {pageContent?.story?.text || '2015 yılında kurulan Ener Otomasyon...'}
          </p>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Mission */}
            <div className="bg-white rounded-xl p-8 shadow-sm" data-testid="mission-card">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {pageContent?.mission?.title || 'Misyonumuz'}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {pageContent?.mission?.text || 'En son teknolojileri kullanarak...'}
              </p>
            </div>

            {/* Vision */}
            <div className="bg-white rounded-xl p-8 shadow-sm" data-testid="vision-card">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Eye className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {pageContent?.vision?.title || 'Vizyonumuz'}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {pageContent?.vision?.text || 'Endüstriyel otomasyon alanında...'}
              </p>
            </div>

            {/* Values */}
            <div className="bg-white rounded-xl p-8 shadow-sm" data-testid="values-card">
              <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Heart className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {pageContent?.values?.title || 'Değerlerimiz'}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {pageContent?.values?.text || 'Dayandığımızın merkezinde...'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;